/* ==========================================================================
   FAQ Accordion Component
   Accessible accordion with smooth max-height animations
   ========================================================================== */

class FaqAccordion {
  /**
   * @param {HTMLElement} container - Element with [data-faq-accordion]
   * @param {Object} options - Configuration options
   */
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      allowMultiple: false, // Only one item open at a time (default)
      animation: true, // Enable smooth height transitions
      initialOpen: null, // Index of initially open item (null = all closed)
      ...options
    };

    this.items = [];
    this.init();
  }

  /**
   * Initialize the accordion - bind events, set initial state
   */
  init() {
    // Query all accordion items
    const itemElements = this.container.querySelectorAll('[data-faq-item]');

    itemElements.forEach((item, index) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const content = item.querySelector('[data-faq-content]');
      const icon = item.querySelector('[data-faq-icon]');

      if (!trigger || !content) {
        console.warn(`FAQ Accordion: Item ${index} missing trigger or content`);
        return;
      }

      // Generate unique IDs for accessibility
      const id = `faq-item-${Date.now()}-${index}`;
      const contentId = `${id}-content`;

      // Set accessibility attributes
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', contentId);
      content.setAttribute('id', contentId);
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', `${id}-trigger`);
      trigger.setAttribute('id', `${id}-trigger`);

      // Ensure trigger is focusable
      if (trigger.tagName !== 'BUTTON') {
        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('role', 'button');
      }

      // Store item reference
      this.items.push({
        element: item,
        trigger,
        content,
        icon,
        isOpen: false
      });

      // Bind click event
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(index);
      });

      // Bind keyboard events (Enter and Space)
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle(index);
        }
      });

      // Initialize content height to 0
      content.style.maxHeight = '0px';
      content.style.overflow = 'hidden';

      if (this.options.animation) {
        content.style.transition = `max-height var(--duration-base, 300ms) ease-out`;
      }
    });

    // Open initial item if specified
    if (this.options.initialOpen !== null && this.items[this.options.initialOpen]) {
      this.open(this.options.initialOpen);
    }

    // Handle window resize - recalculate heights for open items
    this._resizeHandler = this._debounce(() => {
      this.updateHeights();
    }, 150);

    window.addEventListener('resize', this._resizeHandler);

    // Mark as initialized
    this.container.setAttribute('data-faq-initialized', 'true');
  }

  /**
   * Toggle specific item open/closed
   * @param {number} index - Index of item to toggle
   */
  toggle(index) {
    const item = this.items[index];
    if (!item) return;

    if (item.isOpen) {
      this.close(index);
    } else {
      this.open(index);
    }
  }

  /**
   * Open item at index, close others if allowMultiple is false
   * @param {number} index - Index of item to open
   */
  open(index) {
    const item = this.items[index];
    if (!item || item.isOpen) return;

    // Close others if allowMultiple is false
    if (!this.options.allowMultiple) {
      this.items.forEach((otherItem, i) => {
        if (i !== index && otherItem.isOpen) {
          this.close(i);
        }
      });
    }

    // Update state
    item.isOpen = true;

    // Update accessibility
    item.trigger.setAttribute('aria-expanded', 'true');

    // Add open class for icon rotation
    item.element.classList.add('is-open');

    // Animate open - set max-height to scrollHeight
    const scrollHeight = item.content.scrollHeight;
    item.content.style.maxHeight = `${scrollHeight}px`;

    // Dispatch custom event
    this.container.dispatchEvent(new CustomEvent('faq:open', {
      detail: { index, item: item.element }
    }));
  }

  /**
   * Close item at index
   * @param {number} index - Index of item to close
   */
  close(index) {
    const item = this.items[index];
    if (!item || !item.isOpen) return;

    // Update state
    item.isOpen = false;

    // Update accessibility
    item.trigger.setAttribute('aria-expanded', 'false');

    // Remove open class
    item.element.classList.remove('is-open');

    // Animate close - set max-height to 0
    item.content.style.maxHeight = '0px';

    // Dispatch custom event
    this.container.dispatchEvent(new CustomEvent('faq:close', {
      detail: { index, item: item.element }
    }));
  }

  /**
   * Close all accordion items
   */
  closeAll() {
    this.items.forEach((item, index) => {
      if (item.isOpen) {
        this.close(index);
      }
    });
  }

  /**
   * Recalculate content heights for open items (useful after resize or dynamic content)
   */
  updateHeights() {
    this.items.forEach((item) => {
      if (item.isOpen) {
        // Temporarily remove transition for instant update
        const originalTransition = item.content.style.transition;
        item.content.style.transition = 'none';

        // Force reflow
        item.content.offsetHeight;

        // Update height
        item.content.style.maxHeight = `${item.content.scrollHeight}px`;

        // Restore transition
        requestAnimationFrame(() => {
          item.content.style.transition = originalTransition;
        });
      }
    });
  }

  /**
   * Destroy accordion instance - remove event listeners
   */
  destroy() {
    window.removeEventListener('resize', this._resizeHandler);
    this.container.removeAttribute('data-faq-initialized');

    this.items.forEach((item) => {
      item.trigger.removeAttribute('aria-expanded');
      item.trigger.removeAttribute('aria-controls');
      item.content.style.maxHeight = '';
      item.content.style.overflow = '';
      item.content.style.transition = '';
      item.element.classList.remove('is-open');
    });

    this.items = [];
  }

  /**
   * Debounce utility function
   * @private
   */
  _debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

/* ==========================================================================
   Auto-initialization
   ========================================================================== */

/**
 * Initialize all FAQ accordions on the page
 */
function initFaqAccordions() {
  const accordions = document.querySelectorAll('[data-faq-accordion]:not([data-faq-initialized])');

  accordions.forEach((container) => {
    // Check for custom options via data attributes
    const options = {
      allowMultiple: container.dataset.faqAllowMultiple === 'true',
      animation: container.dataset.faqAnimation !== 'false',
      initialOpen: container.dataset.faqInitialOpen ? parseInt(container.dataset.faqInitialOpen, 10) : null
    };

    // Store instance on element for external access
    container.faqAccordion = new FaqAccordion(container, options);
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFaqAccordions);
} else {
  // DOM already loaded
  initFaqAccordions();
}

// Re-initialize when Shopify Theme Editor loads sections
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const accordions = section.querySelectorAll('[data-faq-accordion]:not([data-faq-initialized])');

    accordions.forEach((container) => {
      const options = {
        allowMultiple: container.dataset.faqAllowMultiple === 'true',
        animation: container.dataset.faqAnimation !== 'false',
        initialOpen: container.dataset.faqInitialOpen ? parseInt(container.dataset.faqInitialOpen, 10) : null
      };
      container.faqAccordion = new FaqAccordion(container, options);
    });
  });

  // Cleanup when section is unloaded
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    const accordions = section.querySelectorAll('[data-faq-initialized]');

    accordions.forEach((container) => {
      if (container.faqAccordion && typeof container.faqAccordion.destroy === 'function') {
        container.faqAccordion.destroy();
      }
    });
  });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FaqAccordion;
}
